import React, { useState } from 'react';
import { number, string, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { calculateTipFromPercent, parseTipAmount, DEFAULT_TIP_PERCENTAGES, formatTip } from './tipUtils';
import css from './TipField.module.css';

/**
 * TipField — optional tip selection component for checkout.
 *
 * Usage (in CheckoutPage, before payment button):
 *   {features.tipping && (
 *     <TipField
 *       subtotalCents={subtotal}
 *       currency="EUR"
 *       onTipChange={(amountCents) => setTip(amountCents)}
 *     />
 *   )}
 */
const TipField = ({ subtotalCents, currency, onTipChange, className }) => {
  const [selectedPercent, setSelectedPercent] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [tipCents, setTipCents] = useState(0);

  const handlePercent = (percent) => {
    setSelectedPercent(percent);
    setCustomAmount('');
    const tip = calculateTipFromPercent(subtotalCents, percent);
    setTipCents(tip);
    onTipChange && onTipChange(tip);
  };

  const handleCustom = (e) => {
    const val = e.target.value;
    setCustomAmount(val);
    setSelectedPercent(null);
    const { amountCents } = parseTipAmount(val, currency);
    setTipCents(amountCents);
    onTipChange && onTipChange(amountCents);
  };

  const handleNoTip = () => {
    setSelectedPercent(null);
    setCustomAmount('');
    setTipCents(0);
    onTipChange && onTipChange(0);
  };

  return (
    <div className={classNames(css.root, className)}>
      <h4 className={css.title}><FormattedMessage id="TipField.title" /></h4>
      <p className={css.subtitle}><FormattedMessage id="TipField.subtitle" /></p>
      <div className={css.options}>
        {DEFAULT_TIP_PERCENTAGES.map(pct => (
          <button
            key={pct}
            type="button"
            onClick={() => handlePercent(pct)}
            className={classNames(css.option, { [css.selected]: selectedPercent === pct })}
          >
            {pct}%
            {subtotalCents > 0 && (
              <span className={css.amount}>
                {formatTip(calculateTipFromPercent(subtotalCents, pct), currency)}
              </span>
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={handleNoTip}
          className={classNames(css.option, css.noTip, { [css.selected]: !selectedPercent && !customAmount })}
        >
          <FormattedMessage id="TipField.noTip" />
        </button>
      </div>
      <div className={css.custom}>
        <label className={css.customLabel}><FormattedMessage id="TipField.custom" /></label>
        <input
          type="number"
          value={customAmount}
          onChange={handleCustom}
          placeholder="0.00"
          min="0"
          step="0.01"
          className={css.customInput}
        />
      </div>
      {tipCents > 0 && (
        <p className={css.total}>
          <FormattedMessage id="TipField.total" values={{ amount: formatTip(tipCents, currency) }} />
        </p>
      )}
    </div>
  );
};

TipField.defaultProps = { subtotalCents: 0, currency: 'EUR', onTipChange: null, className: null };
TipField.propTypes = { subtotalCents: number, currency: string, onTipChange: func, className: string };

export default TipField;
